type ConditionRow = {
  client_id?: unknown
  permission_type?: unknown
  old_permission_type?: unknown
  check_flag?: unknown
  request_able?: boolean
  [key: string]: unknown
}

type ConditionData =
  ConditionRow[] | Record<string, ConditionRow> | null | undefined

type AutoRtInfo =
  | {
      clientId?: unknown
      permissionType?: unknown
      oldPermissionType?: unknown
    }
  | null
  | undefined

function toRows(conditionData: ConditionData): ConditionRow[] {
  if (Array.isArray(conditionData)) return conditionData
  if (conditionData && typeof conditionData === 'object')
    return Object.values(conditionData)
  return []
}

/**
 * check_flag === '1' の行の old_permission_type に対応する行を request_able = false に落とす。
 * 対象は「新しい permission に移行済みで不要になった旧 permission の行」のみ。
 * check_flag === '1' の行自体は backend の request_able をそのまま維持する。
 * getDisableRtCondition レスポンス保存直後に呼ぶ。
 *
 * NOTE: 現行コードに呼び出しが無い（送客後処理は buildShouldDisableRow 側）。
 * 未配線の疑いがあるため削除せず残している（配線判断は別タスク）。
 */
// eslint-disable-next-line unused-imports/no-unused-vars, no-unused-vars
function applyCheckFlagDisable<T extends ConditionData>(conditionData: T): T {
  const rows = toRows(conditionData)
  // check_flag=1 の行が持つ (client_id, old_permission_type) のペアを収集
  const disableKeys = new Set<string>()
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue
    if (row.check_flag !== '1') continue
    if (
      row.old_permission_type == null ||
      Number(row.old_permission_type) === 0
    )
      continue
    disableKeys.add(
      `${String(row.client_id)}:${Number(row.old_permission_type)}`
    )
  }
  if (disableKeys.size === 0) return conditionData
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue
    const key = `${String(row.client_id)}:${Number(row.permission_type)}`
    if (disableKeys.has(key)) {
      row.request_able = false
    }
  }
  return conditionData
}

/**
 * updateRtCondition 用: 送客後に request_able = false にすべき行を判定する述語を返す。
 *
 * - 送客行の old_permission_type も同時に落とす（通常RT含む）
 * - 送客行の check_flag === '1' なら同一 client_id の全行を落とす
 * - auto-rt の autoRtInfo.oldPermissionType ペアも落とす
 * - ただし check_flag !== '1' の「新 permission」行は、その old_permission_type 側の
 *   他 permission 送客では落とさない（SetAutoRT + check_flag=0 の再表示を許す）
 */
export function buildShouldDisableRow(
  clientId: unknown,
  permissionType: unknown,
  autoRtInfo: AutoRtInfo,
  conditionData: ConditionData = []
): (row: ConditionRow) => boolean {
  const allRows = toRows(conditionData)
  const sentPermissionType = Number(permissionType)

  const sentRows = allRows.filter(
    (r) =>
      String(r.client_id) === String(clientId) &&
      Number(r.permission_type) === sentPermissionType
  )

  // 送客行が check_flag === '1' なら同一クライアントの全行を抑制
  const suppressWholeClient = sentRows.some((r) => r.check_flag === '1')

  // 無効化する permission_type の集合
  const disablePermTypes = new Set<number>([sentPermissionType])
  // 新 permission 送客時に落とす「旧 permission」。例外で残さない
  const pairOldTypes = new Set<number>()

  // 通常RT / auto_rt 行: 同一 permission の old_permission_type を全て拾う
  // （先頭行だけ見ると auto_rt より先の通常行に old が無く 4 が残る）
  for (const row of sentRows) {
    const rowOldPt = row.old_permission_type
    if (rowOldPt == null || Number(rowOldPt) === 0) continue
    const oldPt = Number(rowOldPt)
    disablePermTypes.add(oldPt)
    pairOldTypes.add(oldPt)
  }

  // auto-RT: autoRtInfo の新旧ペアを落とす
  const shouldSuppressAutoRtPair = Boolean(
    autoRtInfo &&
    autoRtInfo.clientId &&
    String(autoRtInfo.clientId) === String(clientId) &&
    (Number(autoRtInfo.permissionType) === sentPermissionType ||
      Number(autoRtInfo.oldPermissionType) === sentPermissionType)
  )
  if (shouldSuppressAutoRtPair && autoRtInfo) {
    disablePermTypes.add(Number(autoRtInfo.permissionType))
    if (Number(autoRtInfo.oldPermissionType) !== 0) {
      const oldPt = Number(autoRtInfo.oldPermissionType)
      disablePermTypes.add(oldPt)
      pairOldTypes.add(oldPt)
    }
  }

  return (row: ConditionRow): boolean => {
    if (String(row.client_id) !== String(clientId)) return false

    // 送客された permission 自体は常に落とす
    if (Number(row.permission_type) === sentPermissionType) return true

    // 送客行が check_flag=1 なら同一 client 全落ち
    if (suppressWholeClient) return true

    if (!disablePermTypes.has(Number(row.permission_type))) return false

    // auto_rt 新 permission 送客の旧側は例外なしで落とす
    if (pairOldTypes.has(Number(row.permission_type))) return true

    // 波及無効化の例外:
    // check_flag !== '1' かつ old_permission_type が今回の送客 permission である行
    // （＝他 permission 送客時の「新 permission / check_flag=0」側）は残す
    if (
      String(row.check_flag) !== '1' &&
      row.old_permission_type != null &&
      Number(row.old_permission_type) !== 0 &&
      Number(row.old_permission_type) === sentPermissionType
    ) {
      return false
    }

    return true
  }
}

/**
 * 送客確定時: shouldDisableRow が true の行を条件 Object List から物理削除する。
 * 配列はそのまま filter、オブジェクトは残行を詰め直した新オブジェクトを返す。
 */
export function removeDisabledRtConditionRows<T extends ConditionData>(
  conditionData: T,
  shouldDisableRow: (row: ConditionRow) => boolean
): T {
  if (!conditionData || typeof conditionData !== 'object') return conditionData

  if (Array.isArray(conditionData)) {
    return conditionData.filter((row) => {
      if (!row || typeof row !== 'object') return true
      return !shouldDisableRow(row)
    }) as T
  }

  const next: Record<string, ConditionRow> = {}
  let i = 0
  for (const _key in conditionData) {
    const row = (conditionData as Record<string, ConditionRow>)[_key]
    if (!row || typeof row !== 'object') continue
    if (shouldDisableRow(row)) continue
    next[String(i++)] = row
  }
  return next as T
}
